/** Zentrale Definition aller API-Aufrufe. */
import type { ApiClient } from '../lib/apiClient.js';
import type {
  AllocationKey,
  AssetCollectionsResponse,
  AssetDetail,
  CatalogRequestResult,
  CatalogRequestRow,
  DirectoryPerson,
  SoftwareRequestInput,
  SoftwareRequestResult,
  SoftwareRequestRow,
  TodoSummary,
  UserRequestForm,
  UserRequestResult,
  UserRequestRow,
  OrphanReport,
  AssetSummary,
  AssetStatus,
  AssetUpdateInput,
  AssetsResponse,
  AssetRow,
  CatalogCategoryInput,
  CatalogCategoryRow,
  CatalogImageRow,
  CatalogItemInput,
  CatalogItemKind,
  CatalogItemRow,
  CatalogItemsResponse,
  CatalogPublishStatus,
  CatalogSupportStatus,
  AssignmentRow,
  AuditLogRow,
  ConnectorDescriptor,
  CostItemInput,
  CostItemRow,
  CountryReport,
  DashboardSummary,
  MeResponse,
  Paged,
  PeopleResponse,
  ProductPriceRow,
  ProviderRow,
  ReportFilter,
  SyncRunDetail,
  SyncRunRow,
  SyncStatusResponse,
  TrendPoint,
  MyLicensesResponse,
} from './types.js';

function query(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export const api = {
  me: (client: ApiClient, signal?: AbortSignal) => client.get<MeResponse>('/api/me', signal),

  dashboard: (client: ApiClient, options: ReportFilter = {}, signal?: AbortSignal) =>
    client.get<DashboardSummary>(`/api/dashboard/summary${query(options)}`, signal),

  trend: (client: ApiClient, options: ReportFilter & { days?: number; skuId?: string } = {}, signal?: AbortSignal) =>
    client.get<{ points: TrendPoint[] }>(`/api/dashboard/trend${query(options)}`, signal),

  countries: (
    client: ApiClient,
    options: ReportFilter & { skuId?: string; allocationKey?: AllocationKey } = {},
    signal?: AbortSignal,
  ) => client.get<CountryReport>(`/api/reports/countries${query(options)}`, signal),

  assignments: (
    client: ApiClient,
    options: ReportFilter & {
      skuId?: string;
      countryCode?: string;
      onlyDisabled?: boolean;
      /** Freitextfilter je Spalte. */
      product?: string;
      department?: string;
      source?: string;
      search?: string;
      skip?: number;
      take?: number;
    } = {},
    signal?: AbortSignal,
  ) => client.get<Paged<AssignmentRow>>(`/api/reports/assignments${query(options)}`, signal),

  /** Kosten je Person ueber alle Lizenzquellen. */
  people: (
    client: ApiClient,
    options: ReportFilter & { countryCode?: string; onlyPriced?: boolean; search?: string; skip?: number; take?: number } = {},
    signal?: AbortSignal,
  ) => client.get<PeopleResponse>(`/api/reports/people${query(options)}`, signal),

  /**
   * Lizenzen von Personen, die es im Verzeichnis nicht mehr gibt.
   * Eigener Aufruf und nicht Teil des Dashboards: Die Auswertung vergleicht
   * jede Zuweisung mit dem gesamten Verzeichnis.
   */
  orphans: (client: ApiClient, options: { providerId?: string; excludeFree?: boolean } = {}, signal?: AbortSignal) =>
    client.get<OrphanReport>(`/api/reports/orphans${query(options)}`, signal),

  exportCsv: (
    client: ApiClient,
    type: 'skus' | 'countries' | 'assignments' | 'people' | 'orphans',
    options: Record<string, string | boolean | undefined> = {},
  ) => client.download(`/api/reports/export${query({ type, ...options })}`, `${type}.csv`),

  syncStatus: (client: ApiClient, signal?: AbortSignal) =>
    client.get<SyncStatusResponse>('/api/sync/status', signal),

  syncRuns: (client: ApiClient, options: { providerId?: string; skip?: number; take?: number } = {}, signal?: AbortSignal) =>
    client.get<Paged<SyncRunRow>>(`/api/sync/runs${query(options)}`, signal),

  /** Ein einzelner Lauf samt Abschnittsprotokoll – waehrend des Laufs gepollt. */
  syncRun: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.get<SyncRunDetail>(`/api/sync/runs/${encodeURIComponent(id)}`, signal),

  triggerSync: (client: ApiClient, providerId?: string) =>
    client.post<{
      accepted: boolean;
      jobId: string;
      providerId: string | null;
      providerCount: number;
      message: string;
    }>('/api/sync/trigger', providerId ? { providerId } : {}),

  /** Produkte mit Preisen (Administration → Produkte und Preise). */
  prices: (client: ApiClient, options: { providerId?: string; includeRetired?: boolean } = {}, signal?: AbortSignal) =>
    client.get<{ rows: ProductPriceRow[] }>(`/api/manage/prices${query(options)}`, signal),

  setPrice: (
    client: ApiClient,
    skuId: string,
    body: {
      unitCost: number | null;
      currency?: string | null;
      isFree?: boolean;
      allocation?: 'direct' | 'shared';
      allocationCountries?: string[];
      category?: string | null;
      note?: string | null;
    },
  ) => client.put<ProductPriceRow>(`/api/manage/prices/${encodeURIComponent(skuId)}`, body),

  /** Kostenpositionen eines Produkts (je Bezugsweg ein Eintrag). */
  costItems: (client: ApiClient, skuId: string, signal?: AbortSignal) =>
    client.get<{ items: CostItemRow[] }>(`/api/manage/prices/${encodeURIComponent(skuId)}/cost-items`, signal),

  /** Alle Schreibzugriffe liefern die neu berechnete Produktzeile zurueck. */
  addCostItem: (client: ApiClient, skuId: string, body: CostItemInput) =>
    client.post<ProductPriceRow>(`/api/manage/prices/${encodeURIComponent(skuId)}/cost-items`, body),

  updateCostItem: (client: ApiClient, id: string, body: CostItemInput) =>
    client.put<ProductPriceRow>(`/api/manage/cost-items/${encodeURIComponent(id)}`, body),

  deleteCostItem: (client: ApiClient, id: string) =>
    client.deleteJson<ProductPriceRow>(`/api/manage/cost-items/${encodeURIComponent(id)}`),

  connectors: (client: ApiClient, signal?: AbortSignal) =>
    client.get<{ connectors: ConnectorDescriptor[] }>('/api/manage/connectors', signal),

  providers: (client: ApiClient, signal?: AbortSignal) =>
    client.get<{ rows: ProviderRow[] }>('/api/manage/providers', signal),

  createProvider: (
    client: ApiClient,
    body: {
      connectorKey: string;
      slug: string;
      displayName: string;
      description?: string;
      enabled?: boolean;
      config?: Record<string, unknown>;
    },
  ) => client.post<ProviderRow>('/api/manage/providers', body),

  updateProvider: (
    client: ApiClient,
    id: string,
    body: { displayName?: string; description?: string; enabled?: boolean; config?: Record<string, unknown> },
  ) => client.patch<ProviderRow>(`/api/manage/providers/${id}`, body),

  deleteProvider: (client: ApiClient, id: string) => client.delete(`/api/manage/providers/${id}`),

  testProvider: (client: ApiClient, id: string) =>
    client.post<{ ok: boolean; message: string }>(`/api/manage/providers/${id}/test`),

  auditLog: (
    client: ApiClient,
    options: { action?: string; actorOid?: string; from?: string; to?: string; skip?: number; take?: number } = {},
    signal?: AbortSignal,
  ) => client.get<Paged<AuditLogRow>>(`/api/manage/audit${query(options)}`, signal),

  // --- IT-Katalog (Hardware und Software) -----------------------------------

  catalogCategories: (
    client: ApiClient,
    options: { kind?: CatalogItemKind } = {},
    signal?: AbortSignal,
  ) => client.get<{ rows: CatalogCategoryRow[] }>(`/api/catalog/categories${query(options)}`, signal),

  createCatalogCategory: (client: ApiClient, input: CatalogCategoryInput, signal?: AbortSignal) =>
    client.post<CatalogCategoryRow>('/api/catalog/categories', input, signal),

  updateCatalogCategory: (
    client: ApiClient,
    id: string,
    input: Partial<CatalogCategoryInput>,
    signal?: AbortSignal,
  ) => client.put<CatalogCategoryRow>(`/api/catalog/categories/${id}`, input, signal),

  /** Standard ist das Archivieren; `hard` loescht endgueltig (nur ohne Eintraege). */
  deleteCatalogCategory: (client: ApiClient, id: string, hard = false, signal?: AbortSignal) =>
    client.deleteJson<CatalogCategoryRow | { deleted: true }>(
      `/api/catalog/categories/${id}${hard ? '?hard=true' : ''}`,
      signal,
    ),

  catalogItems: (
    client: ApiClient,
    options: {
      kind?: CatalogItemKind;
      categoryId?: string;
      manufacturer?: string;
      supportStatus?: CatalogSupportStatus;
      publishStatus?: CatalogPublishStatus;
      search?: string;
      skip?: number;
      take?: number;
    } = {},
    signal?: AbortSignal,
  ) => client.get<CatalogItemsResponse>(`/api/catalog/items${query(options)}`, signal),

  catalogItem: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.get<CatalogItemRow>(`/api/catalog/items/${id}`, signal),

  createCatalogItem: (client: ApiClient, input: CatalogItemInput, signal?: AbortSignal) =>
    client.post<CatalogItemRow>('/api/catalog/items', input, signal),

  updateCatalogItem: (client: ApiClient, id: string, input: Partial<CatalogItemInput>, signal?: AbortSignal) =>
    client.put<CatalogItemRow>(`/api/catalog/items/${id}`, input, signal),

  deleteCatalogItem: (client: ApiClient, id: string, hard = false, signal?: AbortSignal) =>
    client.deleteJson<CatalogItemRow | { deleted: true }>(`/api/catalog/items/${id}${hard ? '?hard=true' : ''}`, signal),

  uploadCatalogImage: (client: ApiClient, file: File, signal?: AbortSignal) =>
    client.postBinary<CatalogImageRow>(
      `/api/catalog/images${query({ fileName: file.name })}`,
      file,
      signal,
    ),

  catalogImageUrl: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.objectUrl(`/api/catalog/images/${id}`, signal),

  /**
   * Geraete. Der Umfang entsteht serverseitig aus der angemeldeten Identitaet:
   * ohne asset:manage ausschliesslich die eigenen. `canManage` in der Antwort
   * sagt, welcher Fall vorliegt – die Oberflaeche muss das nicht raten.
   *
   * `mine: true` verlangt ausdruecklich nur die eigenen Geraete – noetig auf
   * der Seite "Meine Geraete", weil sonst jede verwaltungsberechtigte Person
   * dort den gesamten Bestand sieht.
   */
  assets: (
    client: ApiClient,
    options: {
      status?: AssetStatus;
      source?: string;
      accountId?: string;
      mine?: boolean;
      /** true = nur Kollektionsgeräte, false = nur Personen und Lager, weggelassen = alle. */
      inCollection?: boolean;
      /** Genau diese Kollektion (voller Pfad). */
      collection?: string;
      includeDeleted?: boolean;
      search?: string;
      skip?: number;
      take?: number;
    } = {},
    signal?: AbortSignal,
  ) => client.get<AssetsResponse>(`/api/assets${query(options)}`, signal),

  /** Die Asset Collections (Tische, Räume) mit Zusammensetzung – nur für die Verwaltung. */
  assetCollections: (client: ApiClient, signal?: AbortSignal) =>
    client.get<AssetCollectionsResponse>('/api/asset-collections', signal),

  /**
   * Warenkorb absenden. Die Antwort sagt, ob daraus schon ein Ticket wurde –
   * gespeichert ist die Anfrage in jedem Fall.
   */
  createCatalogRequest: (
    client: ApiClient,
    input: {
      items: Array<{ catalogItemId: string; quantity: number; note?: string | null; orderFieldValue?: string | null }>;
      note?: string | null;
      /** Entfällt bei Freigabeberechtigten (Rolle CatalogLead). */
      approverExternalId?: string | null;
      approverSource?: 'MANAGER' | 'CHOSEN' | null;
    },
    signal?: AbortSignal,
  ) => client.post<CatalogRequestResult>('/api/catalog/requests', input, signal),

  /** Personen aus dem Verzeichnis suchen – für die Auswahl der Freigabe. */
  searchPeople: (client: ApiClient, q: string, signal?: AbortSignal) =>
    client.get<{ rows: DirectoryPerson[] }>(`/api/directory/people?q=${encodeURIComponent(q)}`, signal),

  /** Die eigene vorgesetzte Person – nur ein Vorschlag, kann fehlen. */
  myManager: (client: ApiClient, signal?: AbortSignal) =>
    client.get<{ manager: DirectoryPerson | null }>('/api/directory/manager', signal),

  /** Freigeben. Erst damit entsteht ein Ticket. */
  approveCatalogRequest: (client: ApiClient, id: string, note?: string | null, signal?: AbortSignal) =>
    client.post<CatalogRequestResult>(`/api/catalog/requests/${encodeURIComponent(id)}/approve`, { note: note ?? null }, signal),

  /** Ablehnen. Es entsteht kein Ticket; die Begründung bleibt sichtbar. */
  rejectCatalogRequest: (client: ApiClient, id: string, note?: string | null, signal?: AbortSignal) =>
    client.post<CatalogRequestRow>(`/api/catalog/requests/${encodeURIComponent(id)}/reject`, { note: note ?? null }, signal),

  /** Eigene Anfragen; mit scope=all alle (nur mit catalog:request-manage). */
  catalogRequests: (client: ApiClient, options: { scope?: 'all' | 'approvals'; status?: string } = {}, signal?: AbortSignal) =>
    client.get<{ rows: CatalogRequestRow[]; total?: number }>(`/api/catalog/requests${query(options)}`, signal),

  /** Ticket erneut anlegen, nachdem es beim ersten Versuch misslang. */
  submitCatalogRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<CatalogRequestResult>(`/api/catalog/requests/${encodeURIComponent(id)}/submit`, {}, signal),

  /** Eigene Anfrage zurückziehen, solange kein Ticket daraus wurde. */
  cancelCatalogRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<CatalogRequestRow>(`/api/catalog/requests/${encodeURIComponent(id)}/cancel`, {}, signal),

  /**
   * Antrag auf eine neue Software. Die Antwort sagt, ob daraus schon ein
   * Ticket wurde – gespeichert ist der Antrag in jedem Fall.
   */
  createSoftwareRequest: (client: ApiClient, input: SoftwareRequestInput, signal?: AbortSignal) =>
    client.post<SoftwareRequestResult>('/api/software-requests', input, signal),

  /** Eigene Anträge; mit scope=all alle (nur mit catalog:request-manage). */
  /** Offene Aufgaben der angemeldeten Person (Glocke). */
  myTodos: (client: ApiClient, signal?: AbortSignal) => client.get<TodoSummary>('/api/me/todos', signal),

  /** Formularbeschreibung des Benutzerantrags. */
  userRequestForm: (client: ApiClient, signal?: AbortSignal) => client.get<UserRequestForm>('/api/user-requests/form', signal),

  /** Benutzerantrag stellen; Antworten je Feldschluessel. */
  createUserRequest: (client: ApiClient, answers: Record<string, unknown>, signal?: AbortSignal) =>
    client.post<UserRequestResult>('/api/user-requests', { answers }, signal),

  /** Eigene Benutzerantraege; mit scope=all alle (nur mit catalog:request-manage). */
  userRequests: (client: ApiClient, options: { scope?: 'all'; status?: string } = {}, signal?: AbortSignal) =>
    client.get<{ rows: UserRequestRow[]; total?: number }>(`/api/user-requests${query(options)}`, signal),

  /** Ticket zu einem Benutzerantrag erneut anlegen. */
  submitUserRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<UserRequestResult>(`/api/user-requests/${encodeURIComponent(id)}/submit`, {}, signal),

  softwareRequests: (client: ApiClient, options: { scope?: 'all'; status?: string } = {}, signal?: AbortSignal) =>
    client.get<{ rows: SoftwareRequestRow[]; total?: number }>(`/api/software-requests${query(options)}`, signal),

  /** Ticket erneut anlegen, nachdem es beim ersten Versuch misslang. */
  submitSoftwareRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<SoftwareRequestResult>(`/api/software-requests/${encodeURIComponent(id)}/submit`, {}, signal),

  /** Vorprüfung wiederholen – etwa, nachdem sie eingerichtet wurde. */
  assessSoftwareRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<SoftwareRequestRow>(`/api/software-requests/${encodeURIComponent(id)}/assess`, {}, signal),

  /** Eigenen Antrag zurückziehen, solange kein Ticket daraus wurde. */
  cancelSoftwareRequest: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.post<SoftwareRequestRow>(`/api/software-requests/${encodeURIComponent(id)}/cancel`, {}, signal),

  /** Die Lizenzen der angemeldeten Person; der Umfang entsteht allein aus dem Token. */
  myLicenses: (client: ApiClient, signal?: AbortSignal) => client.get<MyLicensesResponse>('/api/licenses/mine', signal),

  asset: (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.get<AssetDetail>(`/api/assets/${id}`, signal),

  assetSummary: (client: ApiClient, signal?: AbortSignal) =>
    client.get<AssetSummary>('/api/assets/summary', signal),

  updateAsset: (client: ApiClient, id: string, input: AssetUpdateInput, signal?: AbortSignal) =>
    client.put<AssetRow>(`/api/assets/${id}`, input, signal),
};
